// frontend/src/components/admin/AdminOrderDetails.js - FIXED VERSION

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
  Package,
  MapPin,
  Phone,
  Calendar,
  CreditCard,
  Truck,
  User,
  RefreshCw,
  CheckCircle,
  Clock,
  ArrowLeft,
  DollarSign,
  Shield,
  AlertTriangle,
  Tag,
  Edit,
  Ticket,
  AlertCircle
} from 'lucide-react';
import { formatWeightDisplay } from '../cart/WeightDisplay';

const AdminOrderDetails = () => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [statusLoading, setStatusLoading] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrderDetails();
  }, [id]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`/api/v1/admin/order/${id}`);
     
      if (data.success) {
        setOrder(data.order);
        if (data.availableStaff && data.availableStaff.length > 0) {
          setStaffList(data.availableStaff);
        }
        if (data.order.deliveryStaff) {
          setSelectedStaff(data.order.deliveryStaff._id);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error fetching order details');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      setStatusLoading(true);
      const { data } = await axios.put(`/api/v1/admin/order/${id}`, {
        orderStatus: newStatus
      });
      if (data.success) {
        toast.success('Order status updated successfully');
        fetchOrderDetails();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating order status');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleAssignStaff = async () => {
    if (!selectedStaff) {
      toast.error('Please select a delivery staff');
      return;
    }
    try {
      setAssignLoading(true);
      const { data } = await axios.post('/api/v1/admin/assign-delivery-staff', {
        orderId: id,
        deliveryStaffId: selectedStaff
      });
      if (data.success) {
        toast.success('Delivery staff assigned successfully');
        fetchOrderDetails();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error assigning delivery staff');
    } finally {
      setAssignLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Order Delivered':
        return 'bg-green-100 text-green-800';
      case 'Order Out for Delivery':
        return 'bg-blue-100 text-blue-800';
      case 'Order Packed':
        return 'bg-yellow-100 text-yellow-800';
      case 'Payment Pending':
        return 'bg-orange-100 text-orange-800';
      case 'Cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const calculateDiscountedPrice = (item) => {
    if (!item.discount?.active) {
      // Handle different price types
      if (item.priceType === 'perDozen') {
        return (item.dozenPrice || item.price);
      } else if (item.priceType === 'perPack') {
        return (item.packPrice || item.price);
      }
      return item.price;
    }
   
    // Handle base price based on price type
    let basePrice = item.price;
    if (item.priceType === 'perDozen') {
      basePrice = item.dozenPrice || item.price;
    } else if (item.priceType === 'perPack') {
      basePrice = item.packPrice || item.price;
    }
   
    // Apply discount
    let discountedPrice = basePrice;
    if (item.discount.type === 'percentage') {
      discountedPrice = basePrice - (basePrice * item.discount.value / 100);
    } else {
      discountedPrice = basePrice - item.discount.value;
    }
   
    return discountedPrice;
  };

  // Calculate the total item discount (product-level discounts)
  const calculateTotalItemDiscount = () => {
    if (!order || !order.orderItems) return 0;
   
    return order.orderItems.reduce((total, item) => {
      if (!item.discount?.active) return total;
     
      let basePrice = item.price;
      if (item.priceType === 'perDozen') {
        basePrice = item.dozenPrice || item.price;
      } else if (item.priceType === 'perPack') {
        basePrice = item.packPrice || item.price;
      }
     
      const originalPrice = basePrice * item.quantity;
      const discountedPrice = calculateDiscountedPrice(item) * item.quantity;
     
      return total + (originalPrice - discountedPrice);
    }, 0);
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // FIXED: Button state logic - this was the main issue
  const getButtonState = (targetStatus) => {
    if (!order || statusLoading) return { disabled: true, className: 'opacity-50 cursor-not-allowed' };

    const currentStatus = order.orderStatus;
    
    // Define status progression order
    const statusProgression = [
      'Order Placed',
      'Order Packed', 
      'Order Out for Delivery',
      'Order Delivered'
    ];

    const currentIndex = statusProgression.indexOf(currentStatus);
    const targetIndex = statusProgression.indexOf(targetStatus);

    // If order is delivered or cancelled, disable all action buttons
    if (currentStatus === 'Order Delivered' || currentStatus === 'Cancelled') {
      return { disabled: true, className: 'opacity-50 cursor-not-allowed' };
    }

    switch (targetStatus) {
      case 'Order Packed':
        // Can only mark as packed if current status is "Order Placed"
        if (currentStatus === 'Order Placed') {
          return { disabled: false, className: 'bg-yellow-500 text-white hover:bg-yellow-600' };
        } else if (currentStatus === 'Order Packed') {
          return { disabled: true, className: 'bg-yellow-100 text-yellow-800 cursor-not-allowed' };
        } else {
          return { disabled: true, className: 'opacity-50 cursor-not-allowed' };
        }

      case 'Order Out for Delivery':
        // Can only mark as out for delivery if packed AND delivery staff assigned
        if (currentStatus === 'Order Packed' && order.deliveryStaff) {
          return { disabled: false, className: 'bg-blue-500 text-white hover:bg-blue-600' };
        } else if (currentStatus === 'Order Out for Delivery') {
          return { disabled: true, className: 'bg-blue-100 text-blue-800 cursor-not-allowed' };
        } else if (currentStatus === 'Order Packed' && !order.deliveryStaff) {
          return { disabled: true, className: 'opacity-50 cursor-not-allowed' };
        } else {
          return { disabled: true, className: 'opacity-50 cursor-not-allowed' };
        }

      case 'Order Delivered':
        // Can only mark as delivered if out for delivery
        if (currentStatus === 'Order Out for Delivery') {
          return { disabled: false, className: 'bg-green-500 text-white hover:bg-green-600' };
        } else if (currentStatus === 'Order Delivered') {
          return { disabled: true, className: 'bg-green-100 text-green-800 cursor-not-allowed' };
        } else {
          return { disabled: true, className: 'opacity-50 cursor-not-allowed' };
        }

      case 'Cancelled':
        // Can cancel if not delivered
        if (currentStatus !== 'Order Delivered') {
          return { disabled: false, className: 'bg-red-500 text-white hover:bg-red-600' };
        } else {
          return { disabled: true, className: 'opacity-50 cursor-not-allowed' };
        }

      default:
        return { disabled: true, className: 'opacity-50 cursor-not-allowed' };
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <RefreshCw className="h-8 w-8 text-[#dc2626] animate-spin" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Package className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">Order not found</h3>
          <p className="mt-1 text-sm text-gray-500">The requested order could not be found.</p>
        </div>
      </div>
    );
  }

  // Calculate total discount (product discounts + coupon discount)
  const totalItemDiscount = calculateTotalItemDiscount();
  const hasCouponDiscount = order.couponDiscount && order.couponDiscount > 0;
  const totalDiscount = totalItemDiscount + (order.couponDiscount || 0);

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/admin/orders')}
            className="inline-flex items-center text-gray-600 hover:text-[#dc2626] transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            <span>Back to Orders</span>
          </button>
         
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(order.orderStatus)}`}>
              {order.orderStatus}
            </span>
          </div>
        </div>

        {/* Order Header */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
          <div className="bg-[#dc2626] px-6 py-4 text-white">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
              <div>
                <h1 className="text-xl font-bold">
                  Order #{order.orderId}
                </h1>
                <p className="text-sm mt-1">
                  <span>{formatDateTime(order.createdAt)}</span>
                </p>
              </div>
              <div className="mt-2 md:mt-0">
                <div className="flex items-center gap-2 bg-white text-[#dc2626] px-3 py-1 rounded-lg shadow-sm">
                  <DollarSign className="h-4 w-4" />
                  <span className="font-bold">₹{order.totalPrice.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Order Actions</h2>
              <div className="flex flex-wrap gap-3">
                {/* FIXED: Updated button logic */}
                {order.orderStatus !== 'Order Delivered' && order.orderStatus !== 'Cancelled' && (
                  <>
                    <button
                      onClick={() => handleStatusChange('Order Packed')}
                      disabled={getButtonState('Order Packed').disabled}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium ${getButtonState('Order Packed').className}`}
                    >
                      {statusLoading ? 'Processing...' : 'Mark as Packed'}
                    </button>
                   
                    <button
                      onClick={() => handleStatusChange('Order Out for Delivery')}
                      disabled={getButtonState('Order Out for Delivery').disabled}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium ${getButtonState('Order Out for Delivery').className}`}
                    >
                      {statusLoading ? 'Processing...' : 'Mark as Out for Delivery'}
                    </button>
                   
                    <button
                      onClick={() => handleStatusChange('Order Delivered')}
                      disabled={getButtonState('Order Delivered').disabled}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium ${getButtonState('Order Delivered').className}`}
                    >
                      {statusLoading ? 'Processing...' : 'Mark as Delivered'}
                    </button>
                   
                    <button
                      onClick={() => handleStatusChange('Cancelled')}
                      disabled={getButtonState('Cancelled').disabled}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium ${getButtonState('Cancelled').className}`}
                    >
                      {statusLoading ? 'Processing...' : 'Cancel Order'}
                    </button>
                  </>
                )}

                {/* Show message when all action buttons are disabled */}
                {(order.orderStatus === 'Order Delivered' || order.orderStatus === 'Cancelled') && (
                  <div className="text-sm text-gray-500 italic">
                    No actions available for {order.orderStatus.toLowerCase()} orders.
                  </div>
                )}
              </div>

              {/* Status-specific messages */}
              {order.orderStatus === 'Order Packed' && !order.deliveryStaff && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800 flex items-center">
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    Please assign a delivery staff to proceed with "Out for Delivery" status.
                  </p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Customer Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                  <User className="h-4 w-4 mr-1 text-[#dc2626]" />
                  Customer Information
                </h3>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <User className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      <span className="text-sm text-gray-500">Name</span>
                      <p className="text-sm font-medium">{order.user.name}</p>
                    </div>
                  </div>
                  {order.user.email && (
                    <div className="flex items-start gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <div>
                        <span className="text-sm text-gray-500">Email</span>
                        <p className="text-sm font-medium">{order.user.email}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Shipping Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                  <MapPin className="h-4 w-4 mr-1 text-[#dc2626]" />
                  Shipping Information
                </h3>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      <span className="text-sm text-gray-500">Address</span>
                      <p className="text-sm font-medium">
                        {order.shippingInfo.address}, {order.shippingInfo.city}, {order.shippingInfo.postalCode}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Phone className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      <span className="text-sm text-gray-500">Phone</span>
                      <p className="text-sm font-medium">{order.shippingInfo.phoneno}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                  <CreditCard className="h-4 w-4 mr-1 text-[#dc2626]" />
                  Payment Information
                </h3>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <CreditCard className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                    <span className="text-sm text-gray-500">Payment Method</span>
                      <p className="text-sm font-medium">{order.paymentInfo.paymentMethod}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Shield className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      <span className="text-sm text-gray-500">Payment Status</span>
                      <p className={`text-sm font-medium ${order.paymentInfo.status === 'succeeded' ? 'text-green-600' : 'text-orange-600'}`}>
                        {order.paymentInfo.status}
                      </p>
                    </div>
                  </div>
                  {order.paymentInfo.id && order.paymentInfo.id !== 'COD_PAYMENT' && (
                    <div className="flex items-start gap-2">
                      <Tag className="h-4 w-4 text-gray-400 mt-0.5" />
                      <div>
                        <span className="text-sm text-gray-500">Transaction ID</span>
                        <p className="text-sm font-medium">{order.paymentInfo.id}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Delivery Staff Assignment */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                  <Truck className="h-4 w-4 mr-1 text-[#dc2626]" />
                  Delivery Staff
                </h3>
               
                {order.deliveryStaff ? (
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <User className="h-4 w-4 text-gray-400 mt-0.5" />
                      <div>
                        <span className="text-sm text-gray-500">Assigned To</span>
                        <p className="text-sm font-medium">{order.deliveryStaffName}</p>
                      </div>
                    </div>
                    {order.deliveryStaff.phone && (
                      <div className="flex items-start gap-2">
                        <Phone className="h-4 w-4 text-gray-400 mt-0.5" />
                        <div>
                          <span className="text-sm text-gray-500">Contact</span>
                          <p className="text-sm font-medium">{order.deliveryStaff.phone}</p>
                        </div>
                      </div>
                    )}
                    {order.orderStatus !== 'Order Delivered' && order.orderStatus !== 'Cancelled' && (
                      <button
                        onClick={() => setSelectedStaff('')}
                        className="mt-2 text-sm text-[#dc2626] hover:text-red-700 flex items-center"
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Change Staff
                      </button>
                    )}
                  </div>
                ) : staffList.length > 0 ? (
                  <>
                    <div className="flex flex-col md:flex-row gap-2">
                      <select
                        value={selectedStaff}
                        onChange={(e) => setSelectedStaff(e.target.value)}
                        className="p-2 border border-gray-300 rounded-md text-sm flex-1"
                        disabled={assignLoading}
                      >
                        <option value="">Select delivery staff</option>
                        {staffList.map(staff => (
                          <option key={staff._id} value={staff._id}>
                            {staff.name} - {staff.loginId}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={handleAssignStaff}
                        disabled={!selectedStaff || assignLoading}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium bg-[#dc2626] text-white hover:bg-red-700
                          ${(!selectedStaff || assignLoading) ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {assignLoading ? 'Assigning...' : 'Assign Staff'}
                      </button>
                    </div>
                    {!selectedStaff && (
                      <p className="text-sm text-yellow-600 mt-2 flex items-center">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Assign delivery staff to proceed with delivery
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-red-600">No delivery staff available for this area.</p>
                )}
              </div>
            </div>

            {/* Discount Information */}
            {(totalItemDiscount > 0 || hasCouponDiscount) && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <Tag className="h-5 w-5 mr-1 text-[#dc2626]" />
                  Discount Information
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  {totalItemDiscount > 0 && (
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-green-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Product Discounts</p>
                        <p className="text-sm text-green-600">
                          Total product discount: ₹{totalItemDiscount.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  )}
                 
                  {hasCouponDiscount && (
                    <div className="flex items-start gap-2">
                      <Ticket className="h-4 w-4 text-green-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          Coupon Discount {order.couponCode ? `(${order.couponCode})` : ''}
                        </p>
                        <p className="text-sm text-green-600">
                          Applied coupon discount: ₹{order.couponDiscount.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  )}
                 
                  {totalDiscount > 0 && (
                    <div className="pt-2 border-t">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-green-600 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Total Discount</p>
                          <p className="text-sm text-green-600">
                            Customer saved a total of ₹{totalDiscount.toFixed(2)} on this order
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Order Items */}
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <Package className="h-5 w-5 mr-1 text-[#dc2626]" />
                Order Items
              </h3>
              <div className="bg-gray-50 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-100">
                      <tr>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Item
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Price Type
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Quantity
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Unit Price
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {order.orderItems.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <img className="h-10 w-10 rounded-full object-cover" src={item.image} alt={item.name} />
                              </div>
                              <div className="ml-3">
                                <div className="text-sm font-medium text-gray-900">{item.name}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {item.priceType === 'perKg' && 'Per Kg'}
                            {item.priceType === 'perDozen' && 'Per Dozen'}
                            {item.priceType === 'perPack' && 'Per Pack'}
                            {item.priceType === 'fixed' && (item.selectedWeight || 'Fixed Weight')}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {formatWeightDisplay(item)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {item.discount?.active ? (
                              <div className="flex flex-col">
                                <span className="text-xs text-gray-500 line-through">
                                  ₹{item.priceType === 'perDozen'
                                    ? (item.dozenPrice || item.price).toFixed(2)
                                    : item.price? (item.packPrice || item.price).toFixed(2)
                                    : item.price.toFixed(2)}
                                </span>
                                <span className="text-sm text-red-600">
                                  ₹{calculateDiscountedPrice(item).toFixed(2)}
                                </span>
                              </div>
                            ) : (
                              <span className="text-sm">
                                ₹{item.priceType === 'perDozen'
                                  ? (item.dozenPrice || item.price).toFixed(2)
                                  : item.priceType === 'perPack'
                                  ? (item.packPrice || item.price).toFixed(2)
                                  : item.price.toFixed(2)}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-[#dc2626]">
                            ₹{(calculateDiscountedPrice(item) * item.quantity).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td colSpan="4" className="px-4 py-3 text-sm text-right font-medium">Items Subtotal:</td>
                        <td className="px-4 py-3 text-sm font-medium">₹{order.itemsPrice.toFixed(2)}</td>
                      </tr>
                      {/* Show product level discount total if any */}
                      {totalItemDiscount > 0 && (
                        <tr>
                          <td colSpan="4" className="px-4 py-3 text-sm text-right font-medium text-green-600">Item Discounts:</td>
                          <td className="px-4 py-3 text-sm font-medium text-green-600">-₹{totalItemDiscount.toFixed(2)}</td>
                        </tr>
                      )}
                      {/* Show coupon discount if applied */}
                      {hasCouponDiscount && (
                        <tr>
                          <td colSpan="4" className="px-4 py-3 text-sm text-right font-medium text-green-600">
                            Coupon Discount {order.couponCode ? `(${order.couponCode})` : ''}:
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-green-600">-₹{order.couponDiscount.toFixed(2)}</td>
                        </tr>
                      )}
                      <tr>
                        <td colSpan="4" className="px-4 py-3 text-sm text-right font-medium">Shipping Charge:</td>
                 
                        <td className="px-4 py-3 text-sm font-medium">
                          {order.shippingPrice > 0
                            ? `₹${order.shippingPrice.toFixed(2)}`
                            : <span className="text-green-600">FREE</span>}
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="4" className="px-4 py-3 text-sm text-right font-bold text-gray-900">Total Amount:</td>
                        <td className="px-4 py-3 text-sm font-bold text-[#dc2626]">₹{order.totalPrice.toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
           
            {/* Order Timeline */}
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <Clock className="h-5 w-5 mr-1 text-[#dc2626]" />
                Order Timeline
              </h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="mr-3 bg-green-100 rounded-full p-1">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Order Placed</p>
                      <p className="text-xs text-gray-500">{formatDateTime(order.createdAt)}</p>
                    </div>
                  </div>
                 
                  {order.orderStatus !== 'Order Placed' && order.orderStatus !== 'Payment Pending' && order.orderStatus !== 'Cancelled' && (
                    <div className="flex items-start">
                      <div className="mr-3 bg-yellow-100 rounded-full p-1">
                        <Package className="h-4 w-4 text-yellow-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Order Packed</p>
                        <p className="text-xs text-gray-500">
                          {order.statusUpdates?.packed ? formatDateTime(order.statusUpdates.packed) : 'Status updated'}
                        </p>
                      </div>
                    </div>
                  )}
                 
                  {(order.orderStatus === 'Order Out for Delivery' || order.orderStatus === 'Order Delivered') && (
                    <div className="flex items-start">
                      <div className="mr-3 bg-blue-100 rounded-full p-1">
                        <Truck className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Out for Delivery</p>
                        <p className="text-xs text-gray-500">
                          {order.statusUpdates?.outForDelivery ? formatDateTime(order.statusUpdates.outForDelivery) : 'Status updated'}
                        </p>
                      </div>
                    </div>
                  )}
                 
                  {order.orderStatus === 'Order Delivered' && (
                    <div className="flex items-start">
                      <div className="mr-3 bg-green-100 rounded-full p-1">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Delivered</p>
                        <p className="text-xs text-gray-500">{formatDateTime(order.deliveredAt)}</p>
                      </div>
                    </div>
                  )}
                 
                  {order.orderStatus === 'Cancelled' && (
                    <div className="flex items-start">
                      <div className="mr-3 bg-red-100 rounded-full p-1">
                        <AlertTriangle className="h-4 w-4 text-red-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Cancelled</p>
                        <p className="text-xs text-gray-500">
                          {order.cancelledAt ? formatDateTime(order.cancelledAt) : 'Order was cancelled'}
                          {order.cancelledBy && ` (by ${order.cancelledBy})`}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOrderDetails;